import { merge } from 'lodash';
import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { DeepPartial } from '../../utils/typings';
import { ForecastContext, ForecastParameters } from './ForecastContext';

export type GOAL = 'pension' | 'house' | 'capital' | 'children' | 'travel' | 'contactDetails';

export type GoalIndicationParameters = {
    selectedGoal?: GOAL;
    selectedExpertLevel?: 1 | 2 | 3 | 4; // 1 = novice, 4 = expert
    risk?: {
        selectedId?: string;
        reduction?: {
            isEnabled?: boolean;
        };
    };
    contactDetailsValid?: boolean;
    goals?: {
        pension?: {
            capitalGoal?: {
                currentAgeInYears?: number;
                retirememtAgeInYears?: number;
                amountPerInterval?: number; // 300,00 per maand
                retirementPeriodInYears?: number; // 23 jaar lang
                interval?: 'monthly' | 'yearly'; // maandelijks
                customAmount?: number; // No, I want to set my own goal
            };
            deposit?: {
                initial?: {
                    amount?: number;
                };
                periodic?: {
                    amount?: number;
                    interval?: 'monthly' | 'yearly';
                };
            };
        };
        house?: {
            capitalGoal?: {
                amount?: number;
                inYears?: number;
            };
            deposit?: {
                initial?: {
                    amount?: number;
                };
                periodic?: {
                    amount?: number;
                    interval?: 'monthly' | 'yearly';
                };
            };
        };
        capital?: {
            capitalGoal?: {
                inYears?: number;
            };
            deposit?: {
                initial?: {
                    amount?: number;
                };
                periodic?: {
                    amount?: number;
                    interval?: 'monthly' | 'yearly';
                };
            };
        };
        children?: {
            capitalGoal?: {
                amountChildren?: number;
                amountPerChild?: number;
                inYears?: number;
            };
            deposit?: {
                initial?: {
                    amount?: number;
                };
                periodic?: {
                    amount?: number;
                    interval?: 'monthly' | 'yearly';
                };
            };
        };
        travel?: {
            capitalGoal?: {
                amount?: number;
                inYears?: number;
            };
            deposit?: {
                initial?: {
                    amount?: number;
                };
                periodic?: {
                    amount?: number;
                    interval?: 'monthly' | 'yearly';
                };
            };
        };
    };
};

type GoalIndicationParametersReducerAction = { type: 'MERGE'; payload: DeepPartial<GoalIndicationParameters> };

const goalIndicationParametersReducer: React.Reducer<
    GoalIndicationParameters,
    { type: 'MERGE'; payload: DeepPartial<GoalIndicationParameters> }
> = (currentParameters: GoalIndicationParameters, action: GoalIndicationParametersReducerAction) => {
    switch (action.type) {
        case 'MERGE':
            return merge({}, currentParameters, action.payload);
    }
};

export const GoalIndicationContext = createContext<{
    parameters: GoalIndicationParameters;
    actions: {
        setParameters: (payload: DeepPartial<GoalIndicationParameters>) => void;
        propagate: () => void;
    };
}>({
    parameters: {},
    actions: {
        setParameters: undefined!,
        propagate: undefined!,
    },
});

export const WithGoalIndicationContext: React.FC<{children?: React.ReactNode}> = ({ children }) => {
    const { forecast } = useContext(ForecastContext);

    const [goalIndicationParameters, setGoalIndicationParameters] = useReducer(goalIndicationParametersReducer, {
        selectedGoal: new URLSearchParams(window.location.search).get('selectedGoal')
            ? (new URLSearchParams(window.location.search).get('selectedGoal') as GOAL)
            : undefined,
        goals: {
            children: { capitalGoal: { amountChildren: 1, inYears: 20 } },
            capital: { capitalGoal: { inYears: 20 } },
            travel: { capitalGoal: { inYears: 5 } },
            house: { capitalGoal: { inYears: 20 } },
            pension: {
                capitalGoal: {
                    interval: 'monthly',
                    currentAgeInYears: 40,
                    retirememtAgeInYears: 68,
                    amountPerInterval: 150000,
                    retirementPeriodInYears: 20,
                },
            },
        },
        risk: { reduction: { isEnabled: false } },
    });

    useEffect(() => {}, []);

    return (
        <GoalIndicationContext.Provider
            value={{
                parameters: goalIndicationParameters,
                actions: {
                    setParameters: (payload) => setGoalIndicationParameters({ type: 'MERGE', payload }),
                    propagate: () => {
                        switch (goalIndicationParameters.selectedGoal) {
                            case 'house':
                                return forecast.actions.setParameters(
                                    propagateHouseParameters(goalIndicationParameters),
                                );
                            case 'children':
                                return forecast.actions.setParameters(
                                    propagateChildrenParameters(goalIndicationParameters),
                                );
                            case 'travel':
                                return forecast.actions.setParameters(
                                    propagateTravelParameters(goalIndicationParameters),
                                );
                            case 'pension':
                                return forecast.actions.setParameters(
                                    propagatePensionParameters(goalIndicationParameters),
                                );
                            case 'capital':
                                return forecast.actions.setParameters(
                                    propagateCapitalParameters(goalIndicationParameters),
                                );
                        }
                    },
                },
            }}
        >
            {children}
        </GoalIndicationContext.Provider>
    );
};

const propagateHouseParameters = (parameters: GoalIndicationParameters): DeepPartial<ForecastParameters> => {
    return {
        ...propagateGeneralParameters(parameters),
        capitalGoal: {
            amount: parameters.goals?.house?.capitalGoal?.amount,
            inYears: parameters.goals?.house?.capitalGoal?.inYears,
        },
        deposits: {
            initial: {
                amount: parameters.goals?.house?.deposit?.initial?.amount,
            },
            periodic: {
                amount: parameters.goals?.house?.deposit?.periodic?.amount,
                interval: parameters.goals?.house?.deposit?.periodic?.interval,
            },
        },
    };
};

const propagateChildrenParameters = (parameters: GoalIndicationParameters): DeepPartial<ForecastParameters> => {
    return {
        ...propagateGeneralParameters(parameters),
        capitalGoal: {
            amount:
                (parameters.goals?.children?.capitalGoal?.amountChildren || 0) *
                (parameters.goals?.children?.capitalGoal?.amountPerChild || 0),
            inYears: parameters.goals?.children?.capitalGoal?.inYears,
        },
        deposits: {
            initial: {
                amount: parameters.goals?.children?.deposit?.initial?.amount,
            },
            periodic: {
                amount: parameters.goals?.children?.deposit?.periodic?.amount,
                interval: parameters.goals?.children?.deposit?.periodic?.interval,
            },
        },
    };
};

const propagatePensionParameters = (parameters: GoalIndicationParameters): DeepPartial<ForecastParameters> => {
    return {
        ...propagateGeneralParameters(parameters),
        capitalGoal: {
            amount: parameters.goals?.pension?.capitalGoal?.customAmount,
            inYears:
                (parameters.goals?.pension?.capitalGoal?.retirememtAgeInYears || 65) -
                (parameters.goals?.pension?.capitalGoal?.currentAgeInYears || 18),
        },
        deposits: {
            initial: {
                amount: parameters.goals?.pension?.deposit?.initial?.amount,
            },
            periodic: {
                amount: parameters.goals?.pension?.deposit?.periodic?.amount,
                interval: parameters.goals?.pension?.deposit?.periodic?.interval,
            },
        },
    };
};

const propagateTravelParameters = (parameters: GoalIndicationParameters): DeepPartial<ForecastParameters> => {
    return {
        ...propagateGeneralParameters(parameters),
        capitalGoal: {
            amount: parameters.goals?.travel?.capitalGoal?.amount,
            inYears: parameters.goals?.travel?.capitalGoal?.inYears,
        },
        deposits: {
            initial: {
                amount: parameters.goals?.travel?.deposit?.initial?.amount,
            },
            periodic: {
                amount: parameters.goals?.travel?.deposit?.periodic?.amount,
                interval: parameters.goals?.travel?.deposit?.periodic?.interval,
            },
        },
    };
};

const propagateCapitalParameters = (parameters: GoalIndicationParameters): DeepPartial<ForecastParameters> => {
    return {
        ...propagateGeneralParameters(parameters),
        capitalGoal: {
            amount: 0,
            inYears: parameters.goals?.capital?.capitalGoal?.inYears,
        },
        deposits: {
            initial: {
                amount: parameters.goals?.capital?.deposit?.initial?.amount,
            },
            periodic: {
                amount: parameters.goals?.capital?.deposit?.periodic?.amount,
                interval: parameters.goals?.capital?.deposit?.periodic?.interval,
            },
        },
    };
};

const propagateGeneralParameters = (parameters: GoalIndicationParameters): DeepPartial<ForecastParameters> => {
    return {
        risk: {
            reduction: {
                isEnabled: parameters.risk?.reduction?.isEnabled,
            },
            profile: {
                id: parameters.risk?.selectedId,
            },
        },
        display: !!parameters.selectedExpertLevel && parameters.selectedExpertLevel > 2 ? 'line' : 'bar',
    };
};
